import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { createCheckoutSession, grantPro } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';

interface ProUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProUpgradeModal: React.FC<ProUpgradeModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [grantSuccess, setGrantSuccess] = useState(false);
  const { isSuperuser, impersonatingUid, refreshProStatus } = useAuth();

  const handleGrantPro = async () => {
    if (!impersonatingUid) return;
    try {
      setLoading(true);
      setError('');
      await grantPro(impersonatingUid);
      setGrantSuccess(true);
      await refreshProStatus();
      setTimeout(() => {
        setGrantSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      setError('Failed to grant pro status.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      setLoading(true);
      setError('');
      trackEvent('begin_checkout');
      const { url } = await createCheckoutSession();

      const opened = window.open(url, '_blank');
      if (opened) {
        setLoading(false);
        onClose();
      } else {
        // Popup blocked — fall back to same-tab navigation
        const resetOnReturn = () => { setLoading(false); };
        window.addEventListener('pageshow', resetOnReturn, { once: true });
        window.location.href = url;
      }
    } catch (err) {
      setError('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            Upgrade to
            <span className="rounded bg-[#1c1917] px-1.5 py-0.5 text-xs font-bold leading-none text-[#faf8f5]">PRO</span>
          </DialogTitle>
          <DialogDescription>
            Unlock tournament simulations and rooting interests with a one-time purchase.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-3">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-foreground">$5</span>
            <span className="text-muted-foreground">one-time payment</span>
          </div>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <span className="text-primary font-bold">&rarr;</span>
              Graph win probability over time and track eliminations
              <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary-foreground">NEW</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary font-bold">&rarr;</span>
              Run unlimited tournament simulations
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary font-bold">&rarr;</span>
              View per-player rooting interests
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary font-bold">&rarr;</span>
              One price. No subscriptions.
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary font-bold">&rarr;</span>
              Remove all ads
            </li>
          </ul>
        </div>

        {error && (
          <div className="text-destructive text-sm">{error}</div>
        )}

        <DialogFooter>
          {isSuperuser && impersonatingUid ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleGrantPro} disabled={loading || grantSuccess} className="bg-orange-500 hover:bg-orange-600">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Granting...
                  </>
                ) : grantSuccess ? (
                  'Pro Granted!'
                ) : (
                  'Grant Pro'
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Maybe Later
              </Button>
              <Button onClick={handleUpgrade} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  'Upgrade Now'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProUpgradeModal;
